import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { useSearchParams } from 'react-router-dom'
import moment from 'moment'
import axios from 'axios'
import { timeSlotsBranchServices } from 'api/services'
import { IResTimeSlot } from 'api/services/timeslots-branches/types'
import { ShowNotification } from 'helpers/showNotice'
import { NoticeStatus } from 'store/ducks/notice/types'
import { generateQRBase64 } from 'helpers/generateQRBase64'
import { useSelector } from 'react-redux'
import { AppState } from 'store/store'
import { Button, TextField } from '@mui/material'
import Confirmation from 'components/Confirmation/Confirmation'
import NumericTerminal from 'components/NumericTerminal/NumericTerminal'
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ScanImg from 'assets/images/scan.png'
import './ReserveQueue.sass'

interface Props {
  data?: IResTimeSlot 
  closeHandler: () => void
  lawTimeSlots?: IResTimeSlot[]
}

interface IPrintCheck {
  firstName: string
  lastName: string
  middleName: string
  date: string
  createdAt: string
  fromTime: string
  toTime: string
  lang: string
  qr: string
  applicationPass: string
}

export const printData = async (data: IPrintCheck) => {
  const url = 'http://127.0.0.1:9000/print';

  try {
    const response = await axios.post(url, data, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
    
    return response.data;
  } catch (error: any) {
    if (axios.isAxiosError(error)) {
      console.error('Ошибка запроса:', error.message);
    } else {
      console.error('Непредвиденная ошибка:', error);
    }
    throw error;
  }
};

const ReserveQueue: React.FC<Props> = ({
  data,
  closeHandler,
  lawTimeSlots
}) => {
  const [applicationPass, setApplicationPass] = useState('')
  const [dateOfBirth, setDateOfBirth] = useState('')
  const [type, setType] = useState('pass')
  const [loading, setLoading] = useState(false)
  const [hasError, setHasError] = useState(false)
  const [searchParams] = useSearchParams();
  const branchId = searchParams.get('branchId');
  const router = useNavigate()
	const { scheduleId } = useParams<{ scheduleId: string }>();
	const { lang } = useSelector((state: AppState) => state.i18n);
  const i18n = useSelector(
		(state: AppState) => state.i18n.data.components.ReserveQueue,
	);
  const confirmationRef = useRef<any>(null)
  const hiddenInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (type === 'pass') {
      setApplicationPass('')
      hiddenInputRef.current?.focus();
    }
  }, [type]);

  const handleWrapperClick = () => {
    if (type === 'pass') {
      hiddenInputRef.current?.focus();
    }
  };

  const handleScannerKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && applicationPass.trim().length > 0) {
      setType('date');
    }
  };


  const deleteTimeSlot = async () => {
    const requestData = {
      applicationPass: applicationPass,
      userDateOfBirth: dateOfBirth.split('.').reverse().join('-')
    }
    try {
      setLoading(true)
      await timeSlotsBranchServices.deleteReservation(requestData)
      await reserveQueue()
    } catch(e: any) {
      if(e?.response?.data?.message === '24 hours exception'){
        ShowNotification('Отмена записи возможна не позднее чем за 24 часа до её начала')
      } else {
        ShowNotification(i18n.error)
      }
    } finally {
      setLoading(false)
    }
  }

  const reserveQueue = async () => {
    if(!scheduleId || !branchId || !data?.id) return
    if(!applicationPass.trim() || dateOfBirth.length !== 10){
      setHasError(true)
      return
    }
    const qr = await generateQRBase64(applicationPass)
    const requestData = {
      date: scheduleId,
      branchId: Number(branchId),
      timeslotId: data?.id,
      applicationPass,
      dateOfBirth: dateOfBirth.split('.').reverse().join('-')
    }
    try {
      setLoading(true)
      const res = await timeSlotsBranchServices.reserveTimeSlot(requestData).then((res) => res.data)
      printData({
        date: moment(res.date).format('DD.MM.YYYY'),
        createdAt: moment(res.createdAt).format('DD.MM.YYYY'),
        fromTime: res.timeslot.fromTime,
        toTime: res.timeslot.toTime,
        lang: lang,
        qr: qr,
        firstName: res?.application?.user?.firstName,
        lastName: res?.application?.user?.lastName,
        middleName: res?.application?.user?.middleName,
        applicationPass: applicationPass
      })
      ShowNotification(i18n.success, NoticeStatus.SUCCESS)
      router('/schedule')
    } catch(e: any) {
      if(e?.response?.data?.message === 'Wrong timeslot for law'){
        ShowNotification(i18n.wrongTimeslot?.replace('{{times}}', lawTimeSlots?.filter(i => i.forLaw).map(i => i.fromTime).join('; ') ?? ''))
      }
      if(e?.response?.data?.message === 'Age validation'){
        ShowNotification(i18n.ageValidation)
      }
      if(e?.response?.data?.message === 'Black list validation'){
        ShowNotification(i18n.blackList)
      }
      if(e?.response?.data?.message === 'Expire 083'){
        ShowNotification(i18n.expire083)
      }
      if(e?.response?.data?.message === 'Expire main doc'){
        ShowNotification(i18n.expirePassport)
      }
      if(e?.response?.data?.message === 'Application is not verified'){
        ShowNotification(i18n.appNotVerified)
      }

      if(e?.response?.data?.message === 'dateOfBirth must be a valid ISO 8601 date string'){
        ShowNotification(i18n.dateError)
      }
      if(e?.response?.data?.statusCode === 404){
        ShowNotification(i18n.notFound)
      }
      if(e?.response?.data?.message === 'You already have an active reservation'){
        confirmationRef.current.onShow()
      }
      if(e?.response?.data?.message === 'Timeslot already passed'){
        ShowNotification(i18n.selectCurrentTime)
      }
      if(e?.response?.data?.message === 'TimeslotBranch already exists'){
        ShowNotification(i18n.conflict)
      }
    } finally {
      setLoading(false)
    }
  }
  

  return (
    <>
      <div 
        className="reserve-queue" 
        onClick={(e) => {
          e.stopPropagation();
          handleWrapperClick();
        }}
      >
        {type === 'date' && (
          <ArrowBackIcon sx={{fontSize: 25}} className='reserve-queue__back' onClick={() => setType('pass')}/>
        )}
        
        <CloseIcon sx={{fontSize: 25}} className='reserve-queue__close' onClick={closeHandler}/>
        <div className="reserve-queue__title">{i18n.title}</div>
        <div className="reserve-queue__subtitle">{scheduleId?.split('-').reverse().join('.')} / {data?.fromTime} - {data?.toTime}</div>
        {type === 'pass' && (
          <>
            <div className="reserve-queue__text">{i18n.qrTitle} </div>
            <input
              ref={hiddenInputRef}
              value={applicationPass}
              onChange={(e) => setApplicationPass(e.target.value)}
              onKeyDown={handleScannerKeyDown}
              style={{
                position: 'absolute',
                opacity: 0,
                pointerEvents: 'none',
                top: 0,
              }}
              autoComplete="off"
            />
            <img src={ScanImg} alt="" className="reserve-queue__img" />
          </>
        )}
        {type === 'date' && (
          <>
            <TextField
              className='reserve-queue__field'
              value={dateOfBirth}
              onChange={(e) => setDateOfBirth(e.target.value)}
              label={i18n.dateOfBirth}
              placeholder={i18n.datePlaceholder}
              error={hasError && dateOfBirth.length !== 10}
              autoComplete='off'
              variant='outlined'
              size='small'
              InputLabelProps={{ shrink: true }}
              autoFocus
            />
            <Button
              variant='contained'
              color='success'
              className='create-application-page__button'
              type='button'
              onClick={reserveQueue}
              disabled={loading}
              size='large'
            >
              {i18n.save}
            </Button>
            <NumericTerminal 
              value={dateOfBirth} 
              setValue={setDateOfBirth} 
              submit={reserveQueue}
            />
          </>
        )}
        <Confirmation
          submit={deleteTimeSlot}
          ref={confirmationRef}
          title={i18n.deleteApp}
        />
      </div>
      
    </>
  )
}

export default ReserveQueue